import { useState } from "react";
import { observer } from "mobx-react-lite";
import { contractorDetailStore } from "@/stores/ContractorDetailStore";
import type { ServiceBreakdown } from "@/stores/ContractorDetailStore";
import { formatChange, formatMoneyRub } from "@shared-utils/helpers";
import { LineAreaChart, BarChart, DonutChart } from "../Charts";

const SERVICE_COLORS = [
  "#93c5fd", // blue-300
  "#fdba74", // orange-300
  "#fde68a", // amber-200
  "#f9a8d4", // pink-300
  "#c4b5fd", // violet-300
  "#a5f3fc", // cyan-200
  "#fca5a5", // red-300
];

// ─── Format helpers ───

function formatMetricValue(
  value: number,
  type: "money" | "count" | "check",
): string {
  if (type === "money") {
    // value in rubles
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн ₽`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)} тыс ₽`;
    return formatMoneyRub(value);
  }
  if (type === "check") return `${Math.round(value).toLocaleString("ru-RU")} ₽`;
  return value.toLocaleString("ru-RU");
}

function changeCls(pct: number): string {
  return pct >= 0
    ? "scd-metric-card__change--positive"
    : "scd-metric-card__change--negative";
}

// ─── Main component ───

const ContractorMetrics: React.FC = observer(() => {
  const { stats, isStatsLoading, statsYear } = contractorDetailStore;
  const [avgCheckHoverIdx, setAvgCheckHoverIdx] = useState<number | null>(null);

  const currentCalendarYear = new Date().getFullYear();
  const currentCalendarMonth = new Date().getMonth() + 1;
  // Past years → show all 12 months (no active-month highlight);
  // current year → limit to current month and highlight it
  const effectiveCurrentMonth =
    statsYear === currentCalendarYear ? currentCalendarMonth : undefined;

  if (isStatsLoading && !stats) {
    return (
      <div className="scd-metrics-wrap">
        <div className="scd-metrics-grid scd-metrics-grid--loading">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="scd-metric-card scd-metric-card--skeleton"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  // Bookings donut
  const appCount = stats.bookings.bySource?.app ?? 0;
  const adminCount = stats.bookings.bySource?.admin ?? 0;
  const totalBk = appCount + adminCount || 1;
  const appPct = Math.round((appCount / totalBk) * 100);
  const bookingSegments = [
    { value: appCount, color: "#3b82f6", label: `Приложение — ${appCount}` },
    { value: adminCount, color: "#e5e7eb", label: `Админка — ${adminCount}` },
  ];

  // Avg check donut
  const byService: ServiceBreakdown[] = stats.averageCheck.byService ?? [];
  const serviceSegments =
    byService.length > 0
      ? byService.map((s, i) => ({
          value: s.percent,
          color: SERVICE_COLORS[i % SERVICE_COLORS.length],
          label: `${s.serviceName} — ${Math.round(s.percent)}%`,
        }))
      : [{ value: 1, color: "#c4b5fd", label: "Средний чек" }];

  const fmtMoney = (v: number) => {
    // v in rubles
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} млн ₽`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)} тыс ₽`;
    return `${Math.round(v).toLocaleString("ru-RU")} ₽`;
  };

  return (
    <div className="scd-metrics-wrap">
      <div className="scd-metrics-grid">
        <div className="scd-metrics-row scd-metrics-row--top">
          {/* ── Оборот ── */}
          <div className="scd-metric-card scd-metric-card--chart">
            <div className="scd-metric-card__top">
              <span className="scd-metric-card__label">Оборот</span>
              <div className="scd-metric-card__vals">
                <span className="scd-metric-card__value">
                  {formatMetricValue(stats.revenue.total, "money")}
                </span>
                <span
                  className={`scd-metric-card__change ${changeCls(stats.revenue.changePercent)}`}
                >
                  {formatChange(stats.revenue.changePercent)}
                </span>
              </div>
            </div>
            <div className="scd-metric-card__viz" style={{ height: 120 }}>
              <LineAreaChart
                style={{ height: "120px" }}
                data={stats.revenue.chart}
                color="#3b82f6"
                formatValue={fmtMoney}
              />
            </div>
          </div>

          {/* ── Бронирования ── */}
          <div className="scd-metric-card scd-metric-card--donut scd-metric-card--center scd-metric-card--bookings">
            <div className="scd-metric-card__top">
              <span className="scd-metric-card__label">Бронирования</span>
              <div className="scd-metric-card__vals">
                <span className="scd-metric-card__value">
                  {formatMetricValue(stats.bookings.total, "count")}
                </span>
              </div>
            </div>
            <div className="scd-metric-card__viz scd-metric-card__viz--col">
              <DonutChart
                segments={bookingSegments}
                totalLabel={`${appPct}%`}
                totalSublabel="из приложения"
                size={120}
                strokeWidth={20}
              />
              <div className="scd-metric-legend scd-metric-legend--col">
                {bookingSegments.map((seg) => (
                  <div key={seg.label} className="scd-metric-legend__item">
                    <span
                      className="scd-metric-legend__dot"
                      style={{ background: seg.color }}
                    />
                    <span className="scd-metric-legend__label">
                      {seg.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="scd-metrics-row scd-metrics-row--bottom">
          {/* ── Средний чек ── */}
          <div className="scd-metric-card scd-metric-card--donut">
            <div className="scd-metric-card__top">
              <span className="scd-metric-card__label">Средний чек</span>
              <div className="scd-metric-card__vals">
                <span className="scd-metric-card__value">
                  {formatMetricValue(stats.averageCheck.total, "check")}
                </span>
                <span
                  className={`scd-metric-card__change ${changeCls(stats.averageCheck.changePercent)}`}
                >
                  {formatChange(stats.averageCheck.changePercent)}
                </span>
              </div>
            </div>
            <div className="scd-metric-card__viz scd-metric-card__viz--row">
              <DonutChart
                segments={serviceSegments}
                totalLabel={formatMetricValue(
                  stats.averageCheck.total,
                  "check",
                )}
                totalSublabel={byService.length > 0 ? "средний чек" : undefined}
                externalHoverIdx={avgCheckHoverIdx}
                onHoverChange={setAvgCheckHoverIdx}
                size={100}
              />
              <div className="scd-metric-legend scd-metric-legend--col scd-metric-legend--flex">
                {byService.length > 0 ? (
                  serviceSegments.map((seg, i) => (
                    <div
                      key={seg.label}
                      className={`scd-metric-legend__item${avgCheckHoverIdx === i ? " scd-metric-legend__item--active" : ""}`}
                      onMouseEnter={() => setAvgCheckHoverIdx(i)}
                      onMouseLeave={() => setAvgCheckHoverIdx(null)}
                      style={{ cursor: "default" }}
                    >
                      <span
                        className="scd-metric-legend__dot"
                        style={{ background: seg.color }}
                      />
                      <span className="scd-metric-legend__label">
                        {seg.label}
                      </span>
                    </div>
                  ))
                ) : (
                  <span className="scd-metric-legend__empty">
                    Данных по услугам нет
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Комиссия ── */}
          <div className="scd-metric-card scd-metric-card--chart scd-metric-card--commission">
            <div className="scd-metric-card__top">
              <span className="scd-metric-card__label">Комиссия</span>
              <div className="scd-metric-card__vals">
                <span className="scd-metric-card__value">
                  {formatMetricValue(stats.commission.total, "money")}
                </span>
                <span
                  className={`scd-metric-card__change ${changeCls(stats.commission.changePercent)}`}
                >
                  {formatChange(stats.commission.changePercent)}
                </span>
              </div>
            </div>
            <div className="scd-metric-card__viz">
              <BarChart
                data={stats.commission.chart}
                color="#3b82f6"
                currentMonth={effectiveCurrentMonth}
                formatValue={fmtMoney}
                height={110}
                barThickness={8}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ContractorMetrics.displayName = "ContractorMetrics";
export default ContractorMetrics;
